import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSubscription } from "./SubscriptionContext";

/**
 * DemoModeContext - Manages account tier restrictions and permissions
 *
 * This context handles TWO DISTINCT restriction modes:
 *
 * 1. DEMO MODE (type === "demo"):
 *    - CRUD operations: LOCKED (read-only)
 *    - Graph visibility: ALL GRAPHS VISIBLE (no locks)
 *    - Purpose: Allow users to explore the platform with sample data
 *
 * 2. FREE PLAN (type === "real" && current_plan === "free"):
 *    - CRUD operations: LOCKED (read-only)
 *    - Graph visibility: TOP 2 GRAPHS VISIBLE, BOTTOM 2 GRAPHS LOCKED
 *    - Purpose: Provide limited access to real company data
 *
 * Administrator override: Users with "administrator_arteh" role can bypass
 * all restrictions in both modes.
 */
interface DemoModeContextType {
	isDemoMode: boolean;
	isFreePlan: boolean;
	currentPlan: string | null;
	companyName: string | null;
	shouldLockGraphs: boolean;
	isLoading: boolean;
	demoCompanyId: string;
	isAdministratorArteh: boolean;
}

const DEMO_COMPANY_ID = process.env.REACT_APP_DEMO_COMPANY_ID || "";

const DemoModeContext = createContext<DemoModeContextType | undefined>(
	undefined,
);

/**
 * Helper function to determine if user is in demo mode
 * Checks both user metadata and active company type
 */
const checkDemoMode = (user: any): boolean => {
	if (!user) return false;

	const userMetadata = user["https://arteh.com.au/user_metadata"];
	const appMetadata = user["https://arteh.com.au/app_metadata"];

	// Check if isDemoMode flag is explicitly set
	if (userMetadata?.isDemoMode === true) {
		return true;
	}

	// Check if active company is demo type
	const activeCompanyId = userMetadata?.activeCompanyId;
	if (activeCompanyId && appMetadata?.companies) {
		const activeCompany = appMetadata.companies.find(
			(c: any) => c.id === activeCompanyId,
		);
		if (activeCompany?.type === "demo") {
			return true;
		}
	}

	// Check if the active company ID matches the demo company ID
	if (activeCompanyId === DEMO_COMPANY_ID) {
		return true;
	}

	return false;
};

/**
 * Helper function to get the active company from user metadata
 */
const getActiveCompany = (user: any) => {
	if (!user) return null;

	const userMetadata = user["https://arteh.com.au/user_metadata"];
	const appMetadata = user["https://arteh.com.au/app_metadata"];

	const activeCompanyId = userMetadata?.activeCompanyId;
	if (activeCompanyId && appMetadata?.companies) {
		return appMetadata.companies.find((c: any) => c.id === activeCompanyId);
	}

	return null;
};

// Removed checkFreePlan and getCurrentPlan - now using SubscriptionContext
// which fetches the real plan data from the backend API

/**
 * Helper function to check if user has administrator_arteh role
 */
const checkIsAdministratorArteh = (user: any): boolean => {
	if (!user) return false;

	// Check both possible role claim locations (with single and double slash)
	const roles =
		user["https://arteh.com.au/roles"] ||
		user["https://arteh.com.au//roles"] ||
		[];

	return roles.includes("administrator_arteh");
};

export const DemoModeProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { user, isLoading: auth0Loading, isAuthenticated } = useAuth0();
	const subscription = useSubscription();

	const [isDemoMode, setIsDemoMode] = useState<boolean>(false);
	const [isFreePlan, setIsFreePlan] = useState<boolean>(false);
	const [currentPlan, setCurrentPlan] = useState<string | null>(null);
	const [companyName, setCompanyName] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isAdministratorArteh, setIsAdministratorArteh] =
		useState<boolean>(false);

	useEffect(() => {
		// Update demo mode and plan state when Auth0 user or subscription changes
		if (!auth0Loading && !subscription.state.isLoading) {
			if (isAuthenticated && user) {
				const demoMode = checkDemoMode(user);
				const activeCompany = getActiveCompany(user);
				const name = activeCompany?.name || null;
				const isAdmin = checkIsAdministratorArteh(user);

				// Get plan from SubscriptionContext (source of truth from backend API)
				const plan = subscription.state.currentPlan;

				// Starter (free) plan = real company on the zero-cost tier.
				// Accept legacy "free" for safety until the migration backfill runs.
				const freePlan =
					activeCompany?.type === "real" &&
					(plan === "starter" ||
						(plan as any) === "free" ||
						plan === null);

				// Debug logging

				setIsDemoMode(demoMode);
				setIsFreePlan(freePlan);
				setCurrentPlan(plan);
				setCompanyName(name);
				setIsAdministratorArteh(isAdmin);

				// Graph locking logic:
				// - Demo mode: NO graph locks (all graphs visible)
				// - Free plan: Bottom 2 graphs locked (top 2 graphs visible)
				// - Premium plan: NO graph locks (all graphs visible)
				const shouldLock = freePlan;

				// Trigger a custom event for other components to listen to
				window.dispatchEvent(
					new CustomEvent("demoModeChanged", {
						detail: {
							isDemoMode: demoMode,
							isFreePlan: freePlan,
							shouldLockGraphs: shouldLock,
							currentPlan: plan,
							companyName: name,
							isAdministratorArteh: isAdmin,
						},
					}),
				);
			} else {
				// Not authenticated or no user, not in demo mode
				setIsDemoMode(false);
				setIsFreePlan(false);
				setCurrentPlan(null);
				setCompanyName(null);
				setIsAdministratorArteh(false);
			}
			setIsLoading(false);
		}
	}, [user, auth0Loading, isAuthenticated, subscription.state]);

	const value: DemoModeContextType = {
		isDemoMode,
		isFreePlan,
		currentPlan,
		companyName,
		// shouldLockGraphs applies ONLY to free plans, NOT demo mode
		// Demo mode users see all graphs, free plan users see only top 2 graphs
		shouldLockGraphs: isFreePlan,
		isLoading: isLoading || auth0Loading,
		demoCompanyId: DEMO_COMPANY_ID,
		isAdministratorArteh,
	};

	return (
		<DemoModeContext.Provider value={value}>
			{children}
		</DemoModeContext.Provider>
	);
};

export const useDemoMode = (): DemoModeContextType => {
	const context = useContext(DemoModeContext);
	if (!context) {
		throw new Error("useDemoMode must be used within a DemoModeProvider");
	}
	return context;
};

// Helper hook to get API headers with demo mode
// This is kept for backward compatibility, though the backend should ideally check the JWT directly
export const useApiHeaders = () => {
	const { isDemoMode } = useDemoMode();

	return (additionalHeaders: Record<string, string> = {}) => ({
		"Content-Type": "application/json",
		...(isDemoMode && { "X-Demo-Mode": "true" }),
		...additionalHeaders,
	});
};
